import * as React from 'react';
import { useEffect, useState } from 'react';
import {
  Button,
  Card,
  ErrorMessage,
  FlexRow,
  Grid,
  TextInput,
  useAsyncEffect,
  RotatingIcon,
  IconType,
  ISelectOption,
} from 'ui-toolkit';
import { IFetchStatus } from 'types/rest/IFetchStatus';
import { LendersTable } from './LendersTable';
import { AddLender } from './AddLender';
import { ILenderCompanyData } from '../../types/lenders/ILenderCompany';
import { doGetAsyncAssociatedLenders, doUpdateAsyncAssociatedLenders } from 'ui-toolkit/associatedLenders/dispatch';
import { useAssociatedLendersContext } from '../../ui-toolkit/associatedLenders/AssociatedLendersProvider';
import { isNil } from 'lodash';

interface IAssociatedLendersContainerProps {
  clientId: string;
  lendersFetchStatus: IFetchStatus;
  lendersError?: Error;
  lenders: ILenderCompanyData[];
  updateListOfLenders: () => void;
}

export const AssociatedLendersContainer: React.FC<IAssociatedLendersContainerProps> = ({
  clientId,
  lenders,
  lendersFetchStatus,
  lendersError,
  updateListOfLenders,
}) => {
  const [
    { associatedLenders, associatedLendersError, associatedLendersFetchStatus, updateAssociatedLendersFetchStatus },
    setLenders,
  ] = useAssociatedLendersContext();
  const [showAddLender, setShowAddLender] = useState(false);
  const [listOfNotAssociatedLenders, setListOfNotAssociatedLenders] = useState<
    Array<ISelectOption<string>> | undefined
  >();
  const [filter, setFilter] = useState<string | null>(null);
  const filteredLenders = (lenders: Array<ISelectOption<string>>) =>
    lenders.filter((lender) => (filter ? lender.label.toLowerCase().includes(filter.toLowerCase()) : true));

  useEffect(() => {
    setListOfNotAssociatedLenders(
      lenders.map((lender) => ({
        label: lender.name || '',
        value: lender.id || '',
      }))
    );
  }, [lenders]);

  useAsyncEffect(async () => {
    if (isNil(associatedLenders[clientId])) {
      await doGetAsyncAssociatedLenders(clientId, setLenders);
    }
  }, [clientId]);

  useEffect(() => {
    if (associatedLenders[clientId] && lenders) {
      const mapAssociatedLenders = {};
      associatedLenders[clientId].forEach((lender: any) => (mapAssociatedLenders[lender.label] = lender));
      const filteredLenders = lenders
        .filter((lender) => !mapAssociatedLenders[lender.name || ''])
        .map((lender) => ({
          label: lender.name || '',
          value: lender.id || '',
        }));
      setListOfNotAssociatedLenders(filteredLenders);
    }
  }, [lenders, associatedLenders, clientId]);

  const getNewListOfLenders = () => {
    updateListOfLenders();
  };

  const toggleShowAddLender = () => {
    setShowAddLender(!showAddLender);
  };
  const onAddExistingLender = async (lender: ISelectOption<string> | null) => {
    if (!lender) {
      return;
    }
    await doUpdateAsyncAssociatedLenders(clientId, [lender.value], [], setLenders);
    toggleShowAddLender();
  };

  const onRemoveLender = async (lenderId: string) => {
    await doUpdateAsyncAssociatedLenders(clientId, [], [lenderId], setLenders);
  };

  return lendersFetchStatus.fetching ? (
    <RotatingIcon type={IconType.Loading} />
  ) : (
    <>
      <ErrorMessage error={lendersError} />
      <Card>
        <Grid>
          {associatedLendersError ? <ErrorMessage message={associatedLendersError} /> : null}
          <Grid columnCount={2}>
            <TextInput value={filter} placeholder={'Search'} onChange={setFilter} />
          </Grid>
          <LendersTable
            data={filteredLenders(associatedLenders[clientId] || [])}
            loading={associatedLendersFetchStatus.fetching}
            removeLender={onRemoveLender}
          />
          {showAddLender ? (
            <AddLender
              listOfLenders={listOfNotAssociatedLenders || []}
              onSelectLender={onAddExistingLender}
              refreshListOfLenders={getNewListOfLenders}
              loading={updateAssociatedLendersFetchStatus.fetching || lendersFetchStatus.fetching}
            />
          ) : (
            <FlexRow>
              <Button intent={'ghost'} isInline onClick={toggleShowAddLender}>
                + Add Lender
              </Button>
            </FlexRow>
          )}
        </Grid>
      </Card>
    </>
  );
};
